/**
 * @lines98/client/lib/daily-played-snapshot — СИНХРОННЫЕ снимки daily-статуса для
 * МГНОВЕННОГО рендера доски дня при mount, БЕЗ промежуточного нейтрального/idle-кадра
 * (instant-ui-hydration §1 + §3). Три снимок-ветки над общим `screen-snapshot`:
 *   1. **played-снимок** — факт «сыграно сегодня» (+ финальная доска) → done СРАЗУ.
 *   2. **idle-маркер** — факт «сегодня подтверждённо НЕ сыграно» (только `dateOfDay`)
 *      → idle СРАЗУ, без нейтрального loading-кадра.
 *   3. **in-progress-маркер** — факт «сегодня есть НЕЗАВЕРШЁННАЯ начатая партия»
 *      (`dateOfDay` + ОПЦИОНАЛЬНЫЙ визуальный снимок доски `board`) → НИКОГДА не рисуем
 *      стартовую страницу доски дня (idle/done); при наличии `board` рисуем доску
 *      ВИЗУАЛЬНО МГНОВЕННО из снимка (БЕЗ кадра «загрузка»), движок восстанавливаем реплеем
 *      в ФОНЕ; без `board` — нейтральный кадр-fallback (owner-bug 2026-06-13 «мелькание
 *      стартовой при возврате к начатой партии» + owner-bug A 2026-06-28 «загрузка доски
 *      дня при КАЖДОМ заходе»; см. ниже + {@link InProgressBoardView}).
 *
 * ЗАЧЕМ / WHY THIS EXISTS (owner-playtest fix 2026-06-10 «мелькание idle→done»;
 * 2026-06-11 second pass «idle-доска мигает при КАЖДОМ заходе»):
 * `services/daily-cache` (источник `played`) — IndexedDB-first, читается ТОЛЬКО
 * async (`loadPlayed`). Поэтому при заходе на уже сыгранную доску экран сначала
 * успевал нарисовать IDLE (стартовая доска + «Играть доску дня»), и лишь когда
 * async-`loadPlayed` ресолвился — резко флипал в DONE. По §1 instant-hydration
 * видимое состояние НЕЛЬЗЯ гейтить на сеть/IDB-await: «сыграно сегодня» нужно знать
 * СИНХРОННО в тот же тик mount. played-снимок — зеркало `played`-факта в localStorage
 * (синхронный read), чтобы экран сразу решил idle vs done без флипа.
 *
 * **idle-маркер (2026-06-11):** played-снимок отвечает «нет записи» одинаково для
 * двух РАЗНЫХ случаев — «точно не сыграно» И «синхронно неизвестно» (снимка нет:
 * pre-#8 / кросс-девайс / очищенный браузер). Из-за этого НЕсыгранная доска на КАЖДОМ
 * заходе шла через нейтральный loading-кадр → boot async-подтверждал «не сыграно» →
 * renderIdle = видимое мелькание loading→idle. Лечение симметрично done-пути: когда
 * boot ОДИН раз async-подтвердил «сегодня не сыграно» (и нет in-progress, который
 * увёл бы на board), пишем date-stamped idle-маркер. Следующий mount читает его
 * СИНХРОННО → рисует idle СРАЗУ, без нейтрального кадра. boot затем — идемпотентный
 * reconcile (no-op при совпадении, как done). Маркер ОБЯЗАН исчезать при «сыграно» и
 * при rollover (иначе вчерашний/устаревший idle) — потому он date-stamped (loadIdle
 * сверяет `dateOfDay`) И чистится из `savePlayed`/`clearPlayed` тем же write-path'ом,
 * что played-снимок (не дрейфует).
 *
 * **in-progress-маркер (owner-bug 2026-06-13 «мелькание стартовой при возврате к начатой
 * партии»):** played-снимок и idle-маркер покрывают два терминальных кадра доски дня
 * (done / idle-приглашение). Но между ними есть ТРЕТЬЕ состояние — «партию НАЧАЛИ, но не
 * доиграли» (refresh-recovery). Раньше синхронный mount его НЕ различал: played-снимка
 * нет (не done), а idle-маркер ОСТАВАЛСЯ выставленным с прошлого захода (до старта) — ведь
 * `saveInProgress` его не снимал (снимался только из `savePlayed` на ФИНИШЕ). Поэтому при
 * возврате к начатой партии (полный boot вкладки / переход на «Доску дня») mount читал
 * idle-маркер синхронно → рисовал ПОЛНУЮ стартовую страницу (доска-превью + описание +
 * кнопки), и лишь когда async-`loadInProgress` (IndexedDB) находил незавершённую партию,
 * экран резко флипал в resume на board. Это и была вспышка. Лечение симметрично played/idle:
 * `saveInProgress` ставит date-stamped in-progress-маркер (и СНИМАЕТ idle-маркер — взаимо-
 * исключающие проекции дня), `clearInProgress`/`savePlayed`/`clearPlayed`/stale-rollover его
 * снимают. Синхронный mount читает его ПЕРВЫМ (приоритет выше played/idle) → НИКОГДА не
 * рисует стартовую страницу для дня с начатой партией: сразу нейтральный кадр → async-resume.
 * Маркер несёт ТОЛЬКО `dateOfDay` (seed+moves — канон в IDB `in_progress`); он лишь снимает
 * гейт «рисовать ли стартовую страницу».
 *
 * **Тонкие ветки над `lib/screen-snapshot`** (модель «ствол → ветки», code-structure
 * §1): persistence / per-player изоляция / версионирование / синхронный read — в общем
 * «стволе» `createScreenSnapshot<T>`. Здесь — только daily-специфика: payload'ы
 * (`PlayedRecord` / `IdleMarker` / `InProgressMarker`), type-guard'ы, daily-friendly
 * обёртки. НЕ дублируют кэш-движок.
 *
 * **Зеркало, не второй источник истины.** `services/daily-cache` остаётся каноном
 * `played` (IDB, переживает clear localStorage, общий с in-progress recovery). Снимки
 * ПИШУТСЯ из того же `savePlayed`/чистятся из `clearPlayed` (один write-path,
 * не дрейфует) и служат ИСКЛЮЧИТЕЛЬНО мгновенному показу. Истина «сыграно/не сыграно» —
 * по-прежнему сервер (атомарный `daily_attempts`); снимки — UX-проекция, как и весь
 * daily-cache (см. его module header «Boundary»). Анти-чит цел: снимки не вход для
 * проверок/начислений, движок/верификация их не импортируют.
 *
 * **Ключ по идентичности игрока (privacy):** снимки хранятся под `playerId` (аноним
 * тоже минтится, см. `services/identity`). Аккаунт A не видит статус аккаунта B;
 * у гостя без identity — пусто (no-op) → холодный путь (нейтральный кадр / скелет).
 *
 * Cross-references:
 *   - lib/screen-snapshot.ts (ствол: per-player snapshot-движок — load/save/clear).
 *   - services/daily-cache.ts (канон `played` в IDB; savePlayed/clearPlayed зеркалят сюда).
 *   - screens/daily-board-screen.ts (consumer: синхронный played/idle-detect при mount).
 *   - .claude/skills/instant-ui-hydration/SKILL.md §1 (не гейтить видимое на сеть) + §3.
 */

import type { CellValue, NextBall } from '@lines98/shared/types/game';

import { createScreenSnapshot } from './screen-snapshot.js';
import type { PlayedRecord } from '../services/daily-cache.js';

// =============================================================================
// Constants + общий снимок-«ствол» (per-player store)
// =============================================================================

/** Префикс ключа; полный ключ = `${PREFIX}${playerId}` (изоляция по игроку). */
const KEY_PREFIX = 'lines98.daily.played.snapshot.v1.';
/**
 * Версия схемы payload (`PlayedRecord`). Несовпадение → снимок считается битым и
 * отбрасывается gracefully (экран дотянет факт server-truth'ом через async boot).
 */
const SCHEMA_VERSION = 1;

/**
 * Per-player snapshot-store для played-факта — поверх общего «ствола»
 * `createScreenSnapshot`. Persistence / изоляция / версионирование / синхронный read
 * живут там; здесь — только тип `PlayedRecord` + его type-guard.
 */
const store = createScreenSnapshot<PlayedRecord>({
  keyPrefix: KEY_PREFIX,
  version: SCHEMA_VERSION,
  isValid: isPlayedRecordSnapshot,
});

/** Префикс ключа idle-маркера (отдельный per-player ключ, не пересекается с played). */
const IDLE_KEY_PREFIX = 'lines98.daily.idle.snapshot.v1.';

/**
 * idle-маркер: «сегодня (для этого `dateOfDay`) доска подтверждённо НЕ сыграна» —
 * минимальный date-stamped факт. НЕ несёт доску/seed (стартовая раскладка приходит из
 * `getCachedDailySeed` синхронно; idle рисуется из неё). Date-stamped → loadIdleMarker
 * сверяет день и сам инвалидируется на rollover (вчерашний idle не показывается).
 */
export interface IdleMarker {
  readonly v: 1;
  /** `date_of_day`, для которого подтверждён статус «не сыграно». */
  readonly dateOfDay: string;
}

/** Per-player snapshot-store для idle-маркера — отдельная ветка над тем же «стволом». */
const idleStore = createScreenSnapshot<IdleMarker>({
  keyPrefix: IDLE_KEY_PREFIX,
  version: SCHEMA_VERSION,
  isValid: isIdleMarker,
});

/** Префикс ключа in-progress-маркера (свой per-player ключ; не пересекается с played/idle). */
const IN_PROGRESS_KEY_PREFIX = 'lines98.daily.inprogress.snapshot.v1.';

/**
 * ВИЗУАЛЬНЫЙ снимок in-progress доски — `grid` + следующие шары (`next`) + текущие
 * `score`/`movesCount`, снятые на момент последнего хода. Позволяет МГНОВЕННО (синхронно)
 * нарисовать доску дня при возврате к незавершённой партии (F5 / переход на др. экран и
 * назад), БЕЗ кадра «загрузка доски дня» и переигровки ходов (owner-bug A 2026-06-28). Это
 * UX-проекция показа: движковое состояние (seed+moves) канон в IDB `in_progress` и
 * восстанавливается реплеем в ФОНЕ; снимок === восстановленное состояние по детерминизму
 * движка (snapshot-вид совпадает байт-в-байт), поэтому передача в live-игру бесшовна.
 * Анти-чит цел: снимок не вход для проверок/начислений — server-truth авторитетен на finish.
 */
export interface InProgressBoardView {
  /** 9×9 grid (row-major) на момент последнего хода. */
  readonly grid: CellValue[][];
  /** 3 следующих шара (ниша + predict-призраки) — как в игре. */
  readonly next: NextBall[];
  /** Текущий счёт (плашка «Очки»). */
  readonly score: number;
  /** Сыграно ходов (плашка «Ходы»). */
  readonly movesCount: number;
}

/**
 * in-progress-маркер: «сегодня (для этого `dateOfDay`) есть НЕЗАВЕРШЁННАЯ начатая партия
 * доски дня». Date-stamped факт; seed/moves НЕ несёт (канон в IDB `in_progress`). Помимо
 * гейта «рисовать ли стартовую страницу» (`dateOfDay`), ОПЦИОНАЛЬНО несёт ВИЗУАЛЬНЫЙ снимок
 * доски (`board`) для мгновенной отрисовки при возврате (см. {@link InProgressBoardView}).
 * `board` опционален: первый ход ещё не снят / очищенный браузер → отсутствует, экран падает
 * на нейтральный кадр-fallback. Date-stamped → loadInProgressMarker сверяет день и сам
 * инвалидируется на rollover (вчерашняя начатая партия не блокирует idle нового дня).
 */
export interface InProgressMarker {
  readonly v: 1;
  /** `date_of_day`, для которого есть незавершённая начатая партия. */
  readonly dateOfDay: string;
  /** Визуальный снимок доски для мгновенной отрисовки (опционален — см. выше). */
  readonly board?: InProgressBoardView;
}

/** Per-player snapshot-store для in-progress-маркера — отдельная ветка над тем же «стволом». */
const inProgressStore = createScreenSnapshot<InProgressMarker>({
  keyPrefix: IN_PROGRESS_KEY_PREFIX,
  version: SCHEMA_VERSION,
  isValid: isInProgressMarker,
});

// =============================================================================
// Public API — тонкие обёртки над общим store
// =============================================================================

/**
 * Прочитать played-снимок ТЕКУЩЕГО игрока СИНХРОННО (для мгновенного решения
 * idle-vs-done при mount). Возвращает запись ТОЛЬКО если её `dateOfDay` совпадает с
 * сегодняшним днём доски — иначе `null` (вчерашний снимок не должен показывать done на
 * новой доске). `null` также если: гость (нет identity), снимка нет, версия/форма не
 * совпали, localStorage недоступен. Чистый read — server-truth остаётся истиной.
 *
 * @param dateOfDay — сегодняшний `date_of_day` (синхронно из `getCachedDailySeed`).
 */
export function loadPlayedSnapshot(dateOfDay: string): PlayedRecord | null {
  const record = store.load();
  if (!record) return null;
  // Stale (rollover) — снимок прошлого дня. Не показываем done на новой доске; чистим,
  // чтобы зеркало не залипало (канон `daily-cache.loadPlayed` сделает то же async).
  if (record.dateOfDay !== dateOfDay) {
    store.clear();
    return null;
  }
  return record;
}

/**
 * Записать played-снимок текущего игрока (зеркало `savePlayed`). Вызывается ВНУТРИ
 * `daily-cache.savePlayed`, чтобы write-path был один и снимок не дрейфовал от IDB-
 * канона. No-op для гостя без identity. Defensive: сбой записи (quota / приватный
 * режим) проглатывается стволом — это UX-кэш, не истина.
 */
export function savePlayedSnapshot(record: PlayedRecord): void {
  store.save(record);
}

/** Удалить played-снимок текущего игрока (зеркало `clearPlayed` / тесты). No-op для гостя. */
export function clearPlayedSnapshot(): void {
  store.clear();
}

/**
 * Прочитать idle-маркер ТЕКУЩЕГО игрока СИНХРОННО (мгновенное решение «рисовать idle
 * сразу vs нейтральный кадр» при mount). `true` ТОЛЬКО если маркер есть И его
 * `dateOfDay` == сегодняшнему. Иначе `false`:
 *   - маркер другого дня (rollover) → ЧИСТИТСЯ (вчерашний idle не показываем) + `false`;
 *   - маркера нет / гость / битый / нет localStorage → `false` (нейтральный кадр, boot
 *     async-резолвит истину и при «не сыграно» поставит маркер на следующий заход).
 * Чистый read — server-truth остаётся истиной (boot-reconcile перетрёт при расхождении).
 *
 * @param dateOfDay — сегодняшний `date_of_day` (синхронно из `getCachedDailySeed`).
 */
export function loadIdleMarker(dateOfDay: string): boolean {
  const marker = idleStore.load();
  if (!marker) return false;
  if (marker.dateOfDay !== dateOfDay) {
    // Stale (rollover) — маркер прошлого дня. Чистим, чтобы не залип вчерашний idle.
    idleStore.clear();
    return false;
  }
  return true;
}

/**
 * Записать idle-маркер «сегодня не сыграно» для текущего игрока. Вызывает ЭКРАН в тот
 * момент, когда boot async-ПОДТВЕРДИЛ «не сыграно» И не ушёл в in-progress recovery
 * (idle — корректный терминальный кадр). Date-stamped → на новый день не сработает.
 * No-op для гостя без identity. Defensive (ствол глотает сбой записи).
 *
 * @param dateOfDay — сегодняшний `date_of_day`.
 */
export function saveIdleMarker(dateOfDay: string): void {
  idleStore.save({ v: 1, dateOfDay });
}

/**
 * Удалить idle-маркер текущего игрока. Вызывается из `savePlayed` (игрок сыграл →
 * idle больше не корректен) и `clearPlayed`/тестов. No-op для гостя.
 */
export function clearIdleMarker(): void {
  idleStore.clear();
}

/**
 * Прочитать in-progress-маркер ТЕКУЩЕГО игрока СИНХРОННО (мгновенное решение «есть ли
 * начатая партия → НЕ рисовать стартовую страницу, сразу resume vs нейтральный кадр»
 * при mount). `true` ТОЛЬКО если маркер есть И его `dateOfDay` == сегодняшнему. Иначе
 * `false`:
 *   - маркер другого дня (rollover) → ЧИСТИТСЯ (вчерашняя начатая партия не блокирует
 *     idle нового дня) + `false`;
 *   - маркера нет / гость / битый / нет localStorage → `false` (нейтральный кадр / обычный
 *     путь: boot async-резолвит played / in-progress / idle).
 * Чистый read — server-truth остаётся истиной (анти-чит цел: маркер — UX-проекция показа,
 * движок/верификацию не трогает; см. module header).
 *
 * @param dateOfDay — сегодняшний `date_of_day` (синхронно из `getCachedDailySeed`).
 */
export function loadInProgressMarker(dateOfDay: string): boolean {
  const marker = inProgressStore.load();
  if (!marker) return false;
  if (marker.dateOfDay !== dateOfDay) {
    // Stale (rollover) — маркер прошлого дня. Чистим, чтобы вчерашняя начатая партия не
    // блокировала idle нового дня (её добивает rollover-логика boot'а отдельно).
    inProgressStore.clear();
    return false;
  }
  return true;
}

/**
 * Прочитать ВИЗУАЛЬНЫЙ снимок in-progress доски ТЕКУЩЕГО игрока СИНХРОННО — для мгновенной
 * отрисовки доски дня при возврате к незавершённой партии, БЕЗ кадра «загрузка» и
 * переигровки ходов (owner-bug A 2026-06-28). Возвращает `board` ТОЛЬКО если маркер есть,
 * его `dateOfDay` == сегодняшнему И снимок присутствует; иначе `null`:
 *   - маркер другого дня (rollover) → ЧИСТИТСЯ + `null` (вчерашняя доска не показывается);
 *   - снимка нет (первый ход ещё не снят / битый / гость / нет localStorage) → `null`
 *     (экран падает на нейтральный кадр-fallback; boot восстановит доску реплеем).
 * Чистый read — server-truth остаётся истиной (анти-чит цел: снимок — UX-проекция показа).
 *
 * @param dateOfDay — сегодняшний `date_of_day` (синхронно из `getCachedDailySeed`).
 */
export function loadInProgressBoard(dateOfDay: string): InProgressBoardView | null {
  const marker = inProgressStore.load();
  if (!marker) return null;
  if (marker.dateOfDay !== dateOfDay) {
    inProgressStore.clear();
    return null;
  }
  return marker.board ?? null;
}

/**
 * Записать in-progress-маркер «сегодня есть начатая незавершённая партия» для текущего
 * игрока. Вызывается ВНУТРИ `daily-cache.saveInProgress` (тот же write-path, что IDB-канон
 * `in_progress`) — чтобы факт «партия начата» был доступен СИНХРОННО при следующем mount
 * (instant-ui-hydration §1). Опциональный `board` — ВИЗУАЛЬНЫЙ снимок доски для мгновенной
 * отрисовки при возврате (см. {@link InProgressBoardView}); без него маркер по-прежнему
 * снимает гейт «рисовать стартовую», но экран рисует нейтральный кадр (fallback). Date-
 * stamped → на новый день не сработает. No-op для гостя.
 *
 * @param dateOfDay — `date_of_day` начатой партии.
 * @param board — визуальный снимок доски (опционален).
 */
export function saveInProgressMarker(dateOfDay: string, board?: InProgressBoardView): void {
  inProgressStore.save({ v: 1, dateOfDay, ...(board ? { board } : {}) });
}

/**
 * Удалить in-progress-маркер текущего игрока. Вызывается из `clearInProgress` (партия
 * завершена/сброшена), `savePlayed` (доска сыграна → done), `clearPlayed`/тестов и при
 * stale-rollover в `loadInProgress`. No-op для гостя.
 */
export function clearInProgressMarker(): void {
  inProgressStore.clear();
}

// =============================================================================
// Type guard
// =============================================================================

/**
 * Грубый guard `PlayedRecord` для снимка: проверяем обязательные поля + строго-если-
 * присутствует опциональные (`shortId`/`finalBoard`). Битый/частичный снимок → `load`
 * отдаёт `null` (экран уходит на холодный путь). Глубокую валидность `finalBoard.grid`
 * (только тип-проверка элементов) держим минимальной — рендер деградирует мягко, а
 * server-truth перетрёт при reconcile.
 */
function isPlayedRecordSnapshot(value: unknown): value is PlayedRecord {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  if ('shortId' in v && v['shortId'] !== null && typeof v['shortId'] !== 'string') {
    return false;
  }
  if ('finalBoard' in v && v['finalBoard'] !== undefined && !isFinalBoardShape(v['finalBoard'])) {
    return false;
  }
  if ('lastRank' in v && v['lastRank'] !== undefined && typeof v['lastRank'] !== 'number') {
    return false;
  }
  return (
    v['v'] === 1 &&
    typeof v['dateOfDay'] === 'string' &&
    typeof v['scoreVerified'] === 'number' &&
    (v['replayId'] === null || typeof v['replayId'] === 'string') &&
    typeof v['timestamp'] === 'number'
  );
}

/** Guard `IdleMarker`: версия + строковый `dateOfDay`. Битый → `load` отдаёт null. */
function isIdleMarker(value: unknown): value is IdleMarker {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return v['v'] === 1 && typeof v['dateOfDay'] === 'string';
}

/**
 * Guard `InProgressMarker`: версия + строковый `dateOfDay` + строго-если-присутствует
 * визуальный снимок `board` (битый snapshot → весь маркер отбрасывается; гейт «партия
 * начата» в этом случае дотянет async-канон IDB при boot). Глубину `board.grid` держим
 * минимальной (тип-проверка элементов) — рендер деградирует мягко.
 */
function isInProgressMarker(value: unknown): value is InProgressMarker {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  if ('board' in v && v['board'] !== undefined && !isInProgressBoardShape(v['board'])) {
    return false;
  }
  return v['v'] === 1 && typeof v['dateOfDay'] === 'string';
}

/** Минимальная форма-проверка `InProgressBoardView` (grid 2D-числа + next-массив + числа). */
function isInProgressBoardShape(value: unknown): boolean {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return (
    Array.isArray(v['grid']) &&
    (v['grid'] as unknown[]).every(
      (row) => Array.isArray(row) && (row as unknown[]).every((c) => typeof c === 'number'),
    ) &&
    Array.isArray(v['next']) &&
    typeof v['score'] === 'number' &&
    typeof v['movesCount'] === 'number'
  );
}

/** Минимальная форма-проверка `FinalBoardSnapshot` (grid 2D-числа + next-массив + числа). */
function isFinalBoardShape(value: unknown): boolean {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return (
    Array.isArray(v['grid']) &&
    (v['grid'] as unknown[]).every(
      (row) => Array.isArray(row) && (row as unknown[]).every((c) => typeof c === 'number'),
    ) &&
    Array.isArray(v['next']) &&
    typeof v['movesCount'] === 'number' &&
    typeof v['finishReason'] === 'string'
  );
}
